import React from 'react';
import type { User } from '@/modules/user/userTypes';
import { PGArrowLeft, PGArrowRight } from '@/resources/assets/images';

interface PaginationProps {
	currentPage: number;
	totalPages: number;
	deltaPages?: number;

	previousPage: (currentPage: number) => void;
	canPreviousPage?: boolean;

	nextPage: (currentPage: number) => void;
	canNextPage?: boolean;

	gotoPage: (page: number) => void;
	cursorPagination?: boolean;
	user?: User;
}

const Pagination = ({
	canPreviousPage,
	previousPage,
	currentPage,
	totalPages,
	deltaPages = 5,
	gotoPage,
	nextPage,
	canNextPage,
	cursorPagination,
	user
}: PaginationProps): JSX.Element => {
	return (
		<div className="inline-flex text-white">
			<button
				type="button"
				className={`dataTable-pagination--arrow p-3 ${
					!canPreviousPage ? 'cursor-not-allowed' : ''
				}`}
				onClick={() => previousPage(currentPage)}
				disabled={!canPreviousPage}
			>
				<PGArrowLeft
					className={canPreviousPage ? 'text-orange' : 'text-grey'}
				/>
			</button>

			<button
				type="button"
				data-is-page-one={currentPage === 1}
				className="mx-2 mt-1 flex h-[35px] w-[35px] items-center justify-center rounded-3xl border border-transparent p-0 text-sm text-white transition-all hover:border-orange hover:text-orange data-[is-page-one=true]:border-orange data-[is-page-one=true]:text-orange"
				onClick={() => gotoPage(0)}
			>
				{1}
			</button>

			{currentPage <= 3 || totalPages <= deltaPages ? (
				<>
					{totalPages > 1 && (
						<button
							type="button"
							data-is-page-two={currentPage === 2}
							className="mx-2 mt-1 flex h-[35px] w-[35px] items-center justify-center rounded-3xl border border-transparent p-0 text-sm text-white transition-all hover:border-orange hover:text-orange data-[is-page-two=true]:border-orange data-[is-page-two=true]:text-orange"
							onClick={() => gotoPage(1)}
						>
							{2}
						</button>
					)}
					{totalPages > 2 && (
						<button
							type="button"
							data-is-page-three={currentPage === 3}
							className="mx-2 mt-1 flex h-[35px] w-[35px] items-center justify-center rounded-3xl border border-transparent p-0 text-sm text-white transition-all hover:border-orange hover:text-orange data-[is-page-three=true]:border-orange data-[is-page-three=true]:text-orange"
							onClick={() => gotoPage(2)}
						>
							{3}
						</button>
					)}

					{totalPages > 3 && (
						<button
							type="button"
							data-is-page-four={currentPage === 4}
							className="mx-2 mt-1 flex h-[35px] w-[35px] items-center justify-center rounded-3xl border border-transparent p-0 text-sm text-white transition-all hover:border-orange hover:text-orange data-[is-page-four=true]:border-orange data-[is-page-four=true]:text-orange"
							onClick={() => gotoPage(3)}
						>
							{4}
						</button>
					)}

					{totalPages > 4 && (
						<button
							type="button"
							data-is-page-five={currentPage === 5}
							className="mx-2 mt-1 flex h-[35px] w-[35px] items-center justify-center rounded-3xl border border-transparent p-0 text-sm text-white transition-all hover:border-orange hover:text-orange data-[is-page-five=true]:border-orange data-[is-page-five=true]:text-orange"
							onClick={() => gotoPage(4)}
						>
							{5}
						</button>
					)}
				</>
			) : (
				<>
					<button
						type="button"
						className="mx-2 mt-1 flex h-[35px] w-[35px] items-center justify-center rounded-3xl border border-transparent p-0 text-sm text-white transition-all hover:border-orange hover:text-orange"
						onClick={() => gotoPage(currentPage - 3)}
					>
						...
					</button>
					<button
						type="button"
						className="mx-2 mt-1 flex h-[35px] w-[35px] items-center justify-center rounded-3xl border border-transparent p-0 text-sm text-white transition-all hover:border-orange hover:text-orange"
						onClick={() => {
							gotoPage(
								currentPage > totalPages - deltaPages + 1
									? totalPages - deltaPages
									: currentPage - 2
							);
						}}
					>
						{currentPage > totalPages - deltaPages + 1
							? totalPages - deltaPages + 1
							: currentPage - 1}
					</button>
					<button
						type="button"
						data-is-page-current={
							currentPage <= totalPages - deltaPages + 2
						}
						className="mx-2 mt-1 flex h-[35px] w-[35px] items-center justify-center rounded-3xl border border-transparent p-0 text-sm text-white transition-all hover:border-orange hover:text-orange data-[is-page-current=true]:border-orange data-[is-page-current=true]:text-orange"
						onClick={() => {
							gotoPage(
								currentPage > totalPages - deltaPages + 1
									? totalPages - deltaPages + 1
									: currentPage - 1
							);
						}}
					>
						{currentPage > totalPages - deltaPages + 1
							? totalPages - deltaPages + 2
							: currentPage}
					</button>
					<button
						type="button"
						data-is-page-current={
							currentPage ===
							(currentPage > totalPages - deltaPages + 1
								? totalPages - deltaPages + 3
								: currentPage + 1)
						}
						className="mx-2 mt-1 flex h-[35px] w-[35px] items-center justify-center rounded-3xl border border-transparent p-0 text-sm text-white transition-all hover:border-orange hover:text-orange data-[is-page-current=true]:border-orange data-[is-page-current=true]:text-orange"
						onClick={() => {
							gotoPage(
								currentPage > totalPages - deltaPages + 1
									? totalPages - deltaPages + 2
									: currentPage
							);
						}}
					>
						{currentPage > totalPages - deltaPages + 1
							? totalPages - deltaPages + 3
							: currentPage + 1}
					</button>
				</>
			)}

			{totalPages > deltaPages && (
				<>
					<button
						type="button"
						data-is-page-second-last={
							currentPage === totalPages - 1
						}
						data-disable-hover={
							user?.features?.platformRedesign &&
							(currentPage + 1) * 50 >= 10000
						}
						className="mx-2 mt-1 h-[35px] w-[35px] rounded-3xl border border-transparent p-0 text-sm transition-all hover:border-orange hover:text-orange data-[is-page-second-last=true]:border-orange data-[is-page-second-last=true]:text-orange data-[disable-hover=true]:hover:border-transparent data-[disable-hover=true]:hover:text-inherit"
						onClick={() => {
							if (
								user?.features?.platformRedesign &&
								(currentPage + 1) * 50 >= 10000
							) {
								return;
							}
							gotoPage(
								currentPage > totalPages - deltaPages + 1
									? totalPages - deltaPages + 3
									: currentPage + 1
							);
						}}
					>
						{currentPage > totalPages - deltaPages + 1
							? totalPages - deltaPages + 4
							: '...'}
					</button>
					{!(
						cursorPagination || user?.features?.platformRedesign
					) && (
						<button
							type="button"
							data-is-page-last={currentPage === totalPages}
							className="mx-2 mt-1 flex h-[35px] w-[35px] items-center justify-center rounded-3xl border border-transparent p-0 text-sm text-white transition-all hover:border-orange hover:text-orange data-[is-page-last=true]:border-orange data-[is-page-last=true]:text-orange"
							onClick={() => {
								gotoPage(totalPages - 1);
							}}
						>
							{totalPages}
						</button>
					)}
				</>
			)}

			<button
				type="button"
				className={`dataTable-pagination--arrow p-3 text-md${
					!canNextPage ? 'cursor-not-allowed' : ''
				}`}
				onClick={() => nextPage(currentPage)}
				disabled={!canNextPage}
			>
				<PGArrowRight
					className={canNextPage ? 'text-orange' : 'text-grey'}
				/>
			</button>
		</div>
	);
};

export default Pagination;
